import { wrapText, escapeHtml } from '../common/utils';
import { ChartLayoutConfig } from '../common/layout-config';
import set from 'lodash/set';
import Highcharts from 'highcharts';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';

export interface ChartExportOptions {
  isExportEnabled(layoutConfig: ChartLayoutConfig): boolean;
  addExportOptions(layoutConfig: ChartLayoutConfig, highchartsOptions: Highcharts.Options): void;
}

export function createDefaultChartExportOptions(): ChartExportOptions {
  return {
    isExportEnabled: (layoutConfig: ChartLayoutConfig) => !!layoutConfig.layoutAttributes.Highcharts &&
      !!layoutConfig.layoutAttributes.Highcharts.exporting &&
      layoutConfig.layoutAttributes.Highcharts.exporting.enabled,
    addExportOptions: (layoutConfig: ChartLayoutConfig, highchartsOptions: Highcharts.Options) => {

      const exporting = !!layoutConfig.layoutAttributes.Highcharts &&
        layoutConfig.layoutAttributes.Highcharts.exporting;

      highchartsOptions.exporting = {
        buttons: {
          contextButton: { menuItems: [] }
        },
        libUrl: undefined,
        error: undefined, // We expect it to throw to decide whether to fall back or not.
        ...(exporting || {})
      };
    
      set(highchartsOptions, 'chart.events', {
        beforePrint: function() {
          document.body.classList.add('printing');
          // eslint:disable-next-line
          const chart = (this as any);
          chart.resetParams = { width: chart.chartWidth, height: chart.chartHeight, tooltip: chart.options.tooltip.enabled };
          chart.options.tooltip.enabled = false;
          //Workaround for enableMouseTracking: https://github.com/highcharts/highcharts/issues/12258
          const series = chart.series[0];
          series?.trackerGroups?.forEach((key: any) => {
            if (series[key]) {
              // we don't always have dataLabelsGroup
              series[key]
                .removeClass('highcharts-tracker')
                .on('mouseover', undefined)
                .on('mouseout', undefined)
                .on('touchstart', undefined);
            }
          });
        
          chart.update({ plotOptions: { series: { enableMouseTracking: false } } });
                  
          if (sourceWidth && sourceHeight) {
            chart.setSize(sourceWidth, sourceHeight, false);
          }
        },
        afterPrint: function() {
          // eslint:disable-next-line
          const chart = (this as any);
          chart.options.tooltip.enabled = chart.resetParams?.tooltip;
          chart.update({ plotOptions: { series: { enableMouseTracking: true } } });
          chart.setSize(null, chart.resetParams.height, false);
          document.body.classList.remove('printing');
        }
      });

      const sourceWidth = !!exporting && exporting.sourceWidth;
      const sourceHeight = !!exporting && exporting.sourceHeight;
      const exportFailedMessage = !!exporting && exporting.exportFailedMessage;
      const fallbackToExportServer = !!exporting && exporting.fallbackToExportServer;

      const lengthLimit = 'contextMenuTextLengthLimit';
      const truncLimit = 'contextMenuTextTruncLimit';
      const menu = ((highchartsOptions.exporting.buttons).contextButton.menuItems);

      const menuItem = {
        text: wrapText(layoutConfig,
          escapeHtml(layoutConfig.text('PrintText')), lengthLimit, truncLimit),
        onclick: function () {
          // eslint:disable-next-line
          const chart = (this as any);
          chart.print();
        }
      } as Highcharts.ExportingMenuObject;

      (menu as Highcharts.ExportingMenuObject[]).push(menuItem);

      const imgMenus = [
        ['ExportPngText', 'image/png'],
        ['ExportJpegText', 'image/jpeg'],
        ['ExportPdfText', 'application/pdf'],
        ['ExportSvgText', 'image/svg+xml']
      ];

      for (let i = 0; i < imgMenus.length; i++) {
        const imgMenuItem = {
          text: wrapText(layoutConfig,
            escapeHtml(layoutConfig.text(imgMenus[i][0])), lengthLimit, truncLimit),
          onclick: ((mimeType: string) => function () {
            try {
              // eslint:disable-next-line
              this.exportChartLocal({
                type: mimeType
              });
            } catch(e) {
              if(!!fallbackToExportServer) {
                // eslint:disable-next-line
                this.exportChart({
                  type: mimeType
                });
              }
              else {
                const toasts = toastFactory();
                toasts.dispatchError(exportFailedMessage);
                console.error(e);
              }
            }
          })(imgMenus[i][1])
        };
        (menu as Highcharts.ExportingMenuObject[]).push(imgMenuItem);
      }
    }
  };
}
